import { defineMessages, type IntlShape } from 'react-intl';

export type ShareConversationDisabledReason =
	| 'rovo-response-in-progress'
	| 'missing-valid-response';

type MessageKeys =
	| 'chatHeaderMoreMenuButtonLabel'
	| 'chatHeaderFeedbackButtonText'
	| 'chatHeaderDebugButtonText'
	| 'chatHeaderDebugIconLabel'
	| 'chatHeaderFeedbackIconLabel'
	| 'chatHeaderGetHelpButtonText'
	| 'chatHeaderGetHelpIconLabel'
	| 'chatHeaderCloseButton'
	| 'chatHeaderRenameButtonText'
	| 'chatHeaderRenameIconLabel'
	| 'chatHeaderDeleteButtonText'
	| 'chatHeaderDeleteIconLabel'
	| 'chatHeaderDesktopAppButtonText'
	| 'chatHeaderDesktopAppIconLabel'
	| 'chatHeaderSlackAppButtonText'
	| 'chatHeaderSlackAppIconLabel'
	| 'chatHeaderTeamsAppButtonText'
	| 'chatHeaderTeamsAppIconLabel'
	| 'chatHeaderChromeExtensionButtonText'
	| 'chatHeaderChromeExtensionIconLabel'
	| 'chatHeaderMobileAppButtonText'
	| 'chatHeaderMobileAppIconLabel'
	| 'chatHeaderMoreMenuBackButtonLabel'
	| 'chatStoreUserMessagesLabel'
	| 'mobileAppSubMenuLabel'
	| 'mobileAppSubMenuTitle'
	| 'chatHeaderSwitchSiteButtonText'
	| 'chatHeaderSettingsButtonText'
	| 'chatHeaderSettingsIconLabel'
	| 'chatHeaderWhatsNewButtonText'
	| 'chatHeaderWhatsNewIconLabel'
	| 'chatHeaderShareButtonText'
	| 'chatHeaderShareIconLabel'
	| 'chatHeaderShareDisabledTooltip'
	| 'chatHeaderShareMissingValidResponseDisabledTooltip'
	| 'chatHeaderCliButtonText';

export const chatHeaderMessages: Record<
	MessageKeys,
	{ id: string; defaultMessage: string; description?: string }
> = defineMessages({
	chatHeaderMoreMenuButtonLabel: {
		id: 'ai-mate.rovo-chat-header.more-menu-button-label',
		defaultMessage: 'More',
		description: 'Label for the header more menu button',
	},
	chatHeaderFeedbackButtonText: {
		id: 'ai-mate.rovo-chat-header.feedback-button-text',
		defaultMessage: 'Feedback',
		description: 'Text for the header feedback button',
	},
	chatHeaderDebugButtonText: {
		id: 'ai-mate.rovo-chat-header.debug-button-label',
		defaultMessage: 'Debug',
		description: 'Label for the header debug button',
	},
	chatHeaderDebugIconLabel: {
		id: 'ai-mate.rovo-chat-header.debug-icon-label',
		defaultMessage: 'Debug',
		description: 'Label for the header debug icon',
	},
	chatHeaderFeedbackIconLabel: {
		id: 'ai-mate.rovo-chat-header.feedback-icon-label',
		defaultMessage: 'Feedback',
		description: 'Label for the header feedback icon',
	},
	chatHeaderGetHelpButtonText: {
		id: 'ai-mate.rovo-chat-header.get-help-button-text',
		defaultMessage: 'Get help',
		description: 'Text for the header get help button',
	},
	chatHeaderGetHelpIconLabel: {
		id: 'ai-mate.rovo-chat-header.get-help-icon-label',
		defaultMessage: 'Get help',
		description: 'Label for the header get help icon',
	},
	chatHeaderCloseButton: {
		id: 'ai-mate.rovo-chat-header.close-chat-header-button-label',
		defaultMessage: 'Close',
		description: 'Label for the header close chat button',
	},
	chatHeaderRenameButtonText: {
		id: 'ai-mate.rovo-chat-header.rename-button-text',
		defaultMessage: 'Rename',
		description: 'Text for the header rename button',
	},
	chatHeaderRenameIconLabel: {
		id: 'ai-mate.rovo-chat-header.rename-icon-label',
		defaultMessage: 'Rename',
		description: 'Label for the header rename icon',
	},
	chatHeaderDeleteButtonText: {
		id: 'ai-mate.rovo-chat-header.delete-button-text',
		defaultMessage: 'Delete',
		description: 'Text for the header delete button',
	},
	chatHeaderDeleteIconLabel: {
		id: 'ai-mate.rovo-chat-header.delete-icon-label',
		defaultMessage: 'Delete',
		description: 'Label for the header delete icon',
	},
	chatHeaderDesktopAppButtonText: {
		id: 'ai-mate.rovo-chat-header.desktop-app-button-text',
		defaultMessage: 'Desktop app',
		description: 'Text for the header desktop app button',
	},
	chatHeaderDesktopAppIconLabel: {
		id: 'ai-mate.rovo-chat-header.desktop-app-icon-label',
		defaultMessage: 'Desktop app',
		description: 'Label for the header desktop app icon',
	},
	chatHeaderSlackAppButtonText: {
		id: 'ai-mate.rovo-chat-header.slack-app-button-text',
		defaultMessage: 'Rovo for Slack',
		description: 'Text for the header Rovo for Slack button',
	},
	chatHeaderSlackAppIconLabel: {
		id: 'ai-mate.rovo-chat-header.slack-app-icon-label',
		defaultMessage: 'Rovo for Slack',
		description: 'Label for the header Rovo for Slack icon',
	},
	chatHeaderTeamsAppButtonText: {
		id: 'ai-mate.rovo-chat-header.teams-app-button-text',
		defaultMessage: 'Rovo for Teams',
		description: 'Text for the header Rovo for Teams button',
	},
	chatHeaderTeamsAppIconLabel: {
		id: 'ai-mate.rovo-chat-header.teams-app-icon-label',
		defaultMessage: 'Rovo for Teams',
		description: 'Label for the header Rovo for Teams icon',
	},
	chatHeaderChromeExtensionButtonText: {
		id: 'ai-mate.rovo-chat-header.chrome-extension-button-text',
		defaultMessage: 'Chrome Extension',
		description: 'Text for the header chrome extension button',
	},
	chatHeaderChromeExtensionIconLabel: {
		id: 'ai-mate.rovo-chat-header.chrome-extension-icon-label',
		defaultMessage: 'Chrome Extension',
		description: 'Label for the header chrome extension icon',
	},
	chatHeaderMobileAppButtonText: {
		id: 'ai-mate.rovo-chat-header.mobile-app-button-text',
		defaultMessage: 'Mobile app',
		description: 'Text for the header mobile app button',
	},
	chatHeaderMobileAppIconLabel: {
		id: 'ai-mate.rovo-chat-header.mobile-app-icon-label',
		defaultMessage: 'Mobile app',
		description: 'Label for the header mobile app icon',
	},
	chatHeaderMoreMenuBackButtonLabel: {
		id: 'ai-mate.rovo-chat-header.more-menu-back-button-label',
		defaultMessage: 'Back',
		description: 'Label for the back button in the more menu sub-menu header',
	},
	chatStoreUserMessagesLabel: {
		id: 'ai-mate.chat-input.store-user-messages.label',
		defaultMessage: 'Record conversation history and context to improve quality',
		description: 'Label for the store user messages checkbox',
	},
	mobileAppSubMenuLabel: {
		id: 'ai-mate.chat-header.mobile-app-sub-menu-label',
		defaultMessage: 'Mobile app',
		description: 'Label for the Mobile app item in the more menu',
	},
	mobileAppSubMenuTitle: {
		id: 'ai-mate.chat-header.mobile-app-sub-menu-title',
		defaultMessage: 'Mobile app',
		description: 'Title for the Mobile app sub-menu header in the more menu',
	},
	chatHeaderSwitchSiteButtonText: {
		id: 'ai-mate.rovo-chat-header.switch-site-button-text',
		defaultMessage: 'Switch site',
		description: 'Text for the header switch site button',
	},
	chatHeaderSettingsButtonText: {
		id: 'ai-mate.rovo-chat-header.settings-button-text',
		defaultMessage: 'Settings and memory',
		description: 'Text for the header settings and memory button',
	},
	chatHeaderSettingsIconLabel: {
		id: 'ai-mate.rovo-chat-header.settings-icon-label',
		defaultMessage: 'Settings and memory',
		description: 'Label for the header settings and memory icon',
	},
	chatHeaderWhatsNewButtonText: {
		id: 'ai-mate.rovo-chat-header.whats-new-button-text',
		defaultMessage: "What's new",
		description: "Text for the header what's new button",
	},
	chatHeaderWhatsNewIconLabel: {
		id: 'ai-mate.rovo-chat-header.whats-new-icon-label',
		defaultMessage: "What's new",
		description: "Label for the header what's new icon",
	},
	chatHeaderShareButtonText: {
		id: 'ai-mate.rovo-chat-header.share-button-text',
		defaultMessage: 'Share',
		description: 'Text for the header share button that opens the share chat dialog',
	},
	chatHeaderShareIconLabel: {
		id: 'ai-mate.rovo-chat-header.share-icon-label',
		defaultMessage: 'Share',
		description: 'Label for the header share icon',
	},
	chatHeaderShareDisabledTooltip: {
		id: 'ai-mate.rovo-chat-header.share-disabled-tooltip',
		defaultMessage: 'You can share once Rovo finishes responding',
		description:
			'Tooltip shown when the share chat button is disabled because Rovo is still generating a response. Tells the user they can share the chat after the response is complete.',
	},
	chatHeaderShareMissingValidResponseDisabledTooltip: {
		id: 'ai-mate.rovo-chat-header.share-missing-valid-response-disabled-tooltip',
		defaultMessage: 'Rovo needs to provide a valid response before you can share',
		description:
			'Tooltip shown when the share chat button is disabled because the current chat does not yet have a completed Rovo response that can be used to create a shareable snapshot.',
	},
	chatHeaderCliButtonText: {
		id: 'ai-mate.rovo-chat-header.cli-button-text',
		defaultMessage: 'Rovo CLI',
		description: 'Text for the header Rovo CLI button',
	},
});

const SHARE_BUTTON_MESSAGE_KEYS = {
	'rovo-response-in-progress': 'chatHeaderShareDisabledTooltip',
	'missing-valid-response': 'chatHeaderShareMissingValidResponseDisabledTooltip',
} as const satisfies Record<ShareConversationDisabledReason, MessageKeys>;

export const getShareButtonLabel = (
	disabledReason: ShareConversationDisabledReason | undefined,
	formatMessage: IntlShape['formatMessage'],
): string =>
	formatMessage(
		chatHeaderMessages[
			disabledReason ? SHARE_BUTTON_MESSAGE_KEYS[disabledReason] : 'chatHeaderShareButtonText'
		],
	);
